import React, { useEffect, useState } from 'react';
import { FileText, Paperclip } from 'lucide-react';
import { listSubmissionsAdmin, gradeSubmissionAdmin, type SubmissionRow } from '../../lib/admin';
import { EmptyState, Panel, Stamp, fieldCls, labelCls } from '../../components/lms/ui';
import { GradingQueueSkeleton } from '../../components/lms/Skeletons';

type Filter = 'submitted' | 'all';

const statusTone = (status: string) => {
  if (status === 'graded') return 'cleared' as const;
  if (status === 'returned') return 'held' as const;
  return 'pending' as const;
};

const GradingQueue: React.FC = () => {
  const [items, setItems] = useState<SubmissionRow[]>([]);
  const [filter, setFilter] = useState<Filter>('submitted');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [grades, setGrades] = useState<Record<string, { grade: string; feedback: string }>>({});
  const [busy, setBusy] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      setItems(await listSubmissionsAdmin(filter));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not open the grading queue.');
    } finally {
      setLoading(false);
    }
  };

  // Reload when the queue filter changes; load is intentionally scoped to that filter.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void load(); }, [filter]);

  const grade = async (s: SubmissionRow, status: 'graded' | 'returned') => {
    const g = grades[s.id];
    const gradeNum = g?.grade === '' || g?.grade == null ? null : Number(g.grade);
    if (status === 'graded' && (gradeNum == null || Number.isNaN(gradeNum))) {
      setError('Enter a mark out of 100 before grading this submission.');
      return;
    }
    setBusy(s.id);
    setError('');
    try {
      await gradeSubmissionAdmin(s.id, gradeNum, g?.feedback || '', status);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the grade.');
    } finally {
      setBusy('');
    }
  };

  if (loading) {
    return <GradingQueueSkeleton />;
  }

  return (
    <div className="mt-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 border-y border-rule py-3">
        <div className="flex flex-wrap gap-1 rounded-xl border border-rule bg-white p-1" role="group" aria-label="Submission filter">
          {(['submitted', 'all'] as const).map((f) => {
            const active = filter === f;
            return (
              <button
                key={f}
                type="button"
                aria-pressed={active}
                onClick={() => setFilter(f)}
                className={`rounded-lg px-3 py-2 text-[12px] font-semibold transition-all ${
                  active ? 'bg-blue-600 text-white shadow-sm shadow-blue-600/20' : 'text-ink-2 hover:bg-blue-50 hover:text-blue-800'
                }`}
              >
                {f === 'submitted' ? 'Needs grading' : 'All submissions'}
              </button>
            );
          })}
        </div>
        <p className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-ink-2">
          {items.length} {items.length === 1 ? 'submission' : 'submissions'} on file
        </p>
      </div>

      {error && (
        <p className="rounded-xl border border-danger/25 bg-danger-soft px-3.5 py-2.5 text-[12px] text-danger" role="alert">{error}</p>
      )}

      {items.length === 0 ? (
        <Panel>
          <EmptyState
            title="Nothing waiting at the grading desk"
            body="Student work submitted from any programme arrives here. Switch to all submissions to review work already graded or returned."
          />
        </Panel>
      ) : (
        items.map((s) => {
          const current = grades[s.id] ?? {
            grade: s.grade != null ? String(s.grade) : '',
            feedback: s.feedback || '',
          };
          return (
            <Panel key={s.id}>
              <div className="flex flex-wrap items-start justify-between gap-4 border-b border-rule px-5 py-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2.5">
                    <Stamp tone={statusTone(s.status)}>{s.status}</Stamp>
                    <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-ink-2">
                      SUB-{s.id.replace(/[^a-zA-Z0-9]/g, '').slice(0, 8).toUpperCase()}
                    </span>
                  </div>
                  <h3 className="mt-2 text-[16px] font-semibold leading-snug tracking-[-0.01em] text-ink">
                    {s.assignment_title}
                  </h3>
                  <p className="mt-1 font-mono text-[9.5px] uppercase tracking-[0.1em] text-ink-2">
                    {s.student_name} · {s.course_title || s.course_id || 'Unassigned'} · {new Date(s.created_at).toLocaleString('en-NG')}
                  </p>
                </div>
                {s.grade != null && (
                  <div className="rounded-xl border border-rule bg-paper/50 px-4 py-2 text-right">
                    <p className="font-mono text-[8.5px] uppercase tracking-[0.14em] text-ink-2">Recorded mark</p>
                    <p className="tnum mt-0.5 font-mono text-[24px] font-semibold leading-none text-ink">
                      {s.grade}<span className="text-[12px] font-normal text-ink-2">/100</span>
                    </p>
                  </div>
                )}
              </div>

              <div className="px-5 py-4">
                <div className="flex items-center gap-2">
                  <FileText className="h-3.5 w-3.5 text-ink-2" strokeWidth={1.5} />
                  <p className="font-mono text-[9px] uppercase tracking-[0.14em] text-ink-2">Submitted work</p>
                </div>
                <p className="mt-2 whitespace-pre-wrap rounded-xl border border-rule bg-paper px-4 py-3 text-[13px] leading-6 text-ink">
                  {s.content || 'No written response was attached.'}
                </p>
                {s.file_url && (
                  <a
                    href={s.file_url}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2.5 inline-flex items-center gap-1.5 text-[12px] font-semibold text-blue-700 hover:underline"
                  >
                    <Paperclip className="h-3.5 w-3.5" strokeWidth={1.5} /> Open attached file
                  </a>
                )}

                <div className="mt-4 grid gap-3 border-t border-rule pt-4 sm:grid-cols-[120px_minmax(0,1fr)_auto] sm:items-end">
                  <div>
                    <label htmlFor={`grade-${s.id}`} className={labelCls}>Mark / 100</label>
                    <input
                      id={`grade-${s.id}`}
                      type="number"
                      min={0}
                      max={100}
                      placeholder="0–100"
                      value={current.grade}
                      onChange={(e) =>
                        setGrades({ ...grades, [s.id]: { grade: e.target.value, feedback: current.feedback } })
                      }
                      className={fieldCls}
                    />
                  </div>
                  <div>
                    <label htmlFor={`feedback-${s.id}`} className={labelCls}>Feedback for the student</label>
                    <input
                      id={`feedback-${s.id}`}
                      placeholder="What was strong, what to rework…"
                      value={current.feedback}
                      onChange={(e) =>
                        setGrades({ ...grades, [s.id]: { grade: current.grade, feedback: e.target.value } })
                      }
                      className={fieldCls}
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => void grade(s, 'graded')}
                      disabled={busy === s.id}
                      className="lms-btn-primary !px-3.5 !py-2 !text-[12px]"
                    >
                      {busy === s.id ? 'Saving…' : 'Record grade'}
                    </button>
                    <button
                      type="button"
                      onClick={() => void grade(s, 'returned')}
                      disabled={busy === s.id}
                      className="lms-btn-secondary !px-3.5 !py-2 !text-[12px]"
                    >
                      Return
                    </button>
                  </div>
                </div>
              </div>
            </Panel>
          );
        })
      )}
    </div>
  );
};

export default GradingQueue;
